import { Dispatch, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import { isReactNativeWebView } from '../../utils/global/helper';
import { getCustomTokenForUser } from '../../services/user';
import { auth } from '../../firebase';
import { AppStateAction } from '../../context/AppContext/context';

function useWebViewAuth(dispatch: Dispatch<AppStateAction>) {
  const signInFromToken = useCallback(async (token: string) => {
    try {
      const customToken = await getCustomTokenForUser(token);
      if (customToken) {
        await auth.signInWithCustomToken(customToken);
      }
    } catch (error) {
      toast.error(
        `Error signing in. Please close and try again. Contact support if the problem persists.`,
      );
    }
  }, []);

  // New method of auth from webview
  useEffect(() => {
    if (!isReactNativeWebView()) return;

    const storedToken = sessionStorage.getItem('porterAccessToken');
    if (storedToken) {
      signInFromToken(storedToken);
      return;
    }

    const handleTokenEvent = (event: Event) => {
      const customEvent = event as CustomEvent<{ token: string }>;
      const { token: newToken } = customEvent.detail || {};
      if (newToken) {
        signInFromToken(newToken);
      }
    };

    window.addEventListener('porterAuthToken', handleTokenEvent, true);

    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('porterAuthToken', handleTokenEvent, true);
    };
  }, [signInFromToken]);

  // Backwards compatibility only
  // TODO: Remove once most users have updated to new app version
  useEffect(() => {
    if (!isReactNativeWebView()) return undefined;

    const authTokenListener = async (event: MessageEvent) => {
      try {
        if (!event.data || typeof event.data !== 'string') return;
        const data = JSON.parse(event.data);
        if (data.type === 'AUTH_TOKEN' && data.token) {
          await signInFromToken(data.token);
        }
      } catch {
        // Do nothing
      }
    };

    window.addEventListener('message', authTokenListener, true);

    return () => {
      window.removeEventListener('message', authTokenListener, true);
    };
  }, [signInFromToken]);

  // Used in DashboardLayout to unblock user in case auth token is not received within 10 seconds
  useEffect(() => {
    if (!isReactNativeWebView()) return;

    const timeout = setTimeout(() => {
      dispatch({ type: 'SET_WEBVIEW_SHOULD_BE_AUTHENTICATED', payload: true });
    }, 10_000);

    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export default useWebViewAuth;
